import { Button, type ButtonProps } from '@chakra-ui/react'
import { faCheck, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { RPCRefresh } from '@/client/rpc'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'
import { useToaster } from '@/components/Toast/useToaster'
import Tippy from '@/components/UI/Tippy'

export default function TopicFollowButton({
  topicId,
  isFollowing,
  customTitle,
  customHoverText,
  customToast,
  variant,
  followSuccessCallback,
}: {
  topicId: number
  isFollowing: boolean
  customTitle?: string
  customHoverText?: string
  customToast?: string
  variant?: ButtonProps['variant']
  followSuccessCallback?: () => void
}): JSX.Element | null {
  const toast = useToaster()

  const hoverText =
    customHoverText ??
    (isFollowing ? 'Unfollow this topic' : 'Get notified about new sessions in this topic')

  const [handleFollowTopic, isFollowingTopic] = useAPIWrapper(
    async () => {
      await RPCRefresh.followTopic({ topicId })
      toast({
        title: customToast ?? 'Successfully followed topic!',
        status: 'success',
      })
      if (followSuccessCallback != null) {
        followSuccessCallback()
      }
    },
    [toast, topicId, customToast, followSuccessCallback],
    'Follow Topic'
  )

  const [handleUnfollowTopic, isUnfollowingTopic] = useAPIWrapper(
    async () => {
      await RPCRefresh.unfollowTopic({ topicId })
      toast({
        title: customToast ?? 'Successfully unfollowed topic!',
        status: 'success',
      })
    },
    [toast, topicId, customToast],
    'Unfollow Topic'
  )

  return (
    <Tippy content={hoverText}>
      <RequireLoginAndApproval>
        {isFollowing ? (
          <Button
            variant="outline"
            minWidth={150}
            width={['100%', 'auto']}
            onClick={handleUnfollowTopic}
            isLoading={isUnfollowingTopic}
            leftIcon={<FontAwesomeIcon icon={faCheck} />}
          >
            {customTitle ?? 'Following'}
          </Button>
        ) : (
          <Button
            minWidth={150}
            width={['100%', 'auto']}
            onClick={handleFollowTopic}
            isLoading={isFollowingTopic}
            leftIcon={<FontAwesomeIcon icon={faPlus} />}
            variant={variant}
          >
            {customTitle ?? 'Follow'}
          </Button>
        )}
      </RequireLoginAndApproval>
    </Tippy>
  )
}
